import {
	getLocalTimeZone,
	now,
	parseDateTime,
	Time,
	toZoned,
	type ZonedDateTime,
} from '@internationalized/date';
import { Button, Checkbox, TimeField } from '@react-spectrum/s2';
import { DatePicker } from '@react-spectrum/s2/DatePicker';
import { style as spectrumStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { getInstagramChannelFromCalendars } from '@/api/ExpressApi';
import { useExpressApiContext } from '@/api/ExpressApiContext';
import { useUserId } from '@/auth/UserIdContext';
import {
	CalendarGlyph,
	EmailThumbnail,
} from '@/components/artifact/ArtifactRow';
import { TYPE_TAGS } from '@/components/artifact/panelMeta';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot.ts';
import { BlobImage } from '@/components/blob/BlobImage';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { useUserSettings } from '@/hooks/useUserSettings';
import { logger } from '@/logger';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout.tsx';
import { uploadSlidesToExpress } from '@/spaces/chat/panels/expressPostSync';
import { trackEvent } from '@/utils/fullstory';

import type {
	Action as ProcessAction,
	RequestOwnerActionConfig,
	TriggerSpec,
} from '../../../../../bridge/types/action.ts';
import {
	type Artifact,
	type ExpressMetadata,
	type InstagramPost,
	type InstagramPostSchedule,
	MONITOR_ARTIFACT_TYPES,
} from '../../../../../bridge/types/artifact.ts';
import {
	ALWAYS_ON_ARTIFACT_TYPES,
	SCHEDULABLE_ARTIFACT_TYPES,
	isAlwaysOnOnlyProcess,
	isMonitorOnlyProcess,
	isOwnerOnlyProcess,
	isSkipped,
} from '../../../../../bridge/utils/setupCardItems.ts';

import { ScheduleBody } from './ScheduleBody.tsx';
import {
	absolutePublishDateIso,
	absoluteScheduleFrom,
	formatScheduleDate,
	formatTriggerSendTime,
	futureDefaultDate,
	isPublishNowSentinel,
	isSameMinute,
	isSchedulableTime,
	isSendMinutePast,
	legacyFireAtIso,
	normalizeToUtcIsoZ,
	parseStoredTriggerTime,
	PAST_PICK_MESSAGE,
	postScheduleIso,
	resolveOnActivationPublishDate,
	rowThumbnailClass,
	scheduleItemTitle,
	usePendingSchedulePick,
	type ActivateMode,
	activateSuccessTitle,
	activateSuccessSummary,
} from './scheduleUtils.ts';
import { SuccessInterstitial } from './SuccessInterstitial.tsx';

interface ProcessSchedulePanelProps {
	processId: string;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

const datePickerStyles = spectrumStyle({ width: 'full' });

const RefreshGlyph = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 20 20"
		fill="currentColor"
		className="size-5 shrink-0 text-gray-500"
		aria-hidden="true"
	>
		<path
			fillRule="evenodd"
			d="M15.312 4.688a7 7 0 00-11.249 2.06.75.75 0 11-1.403-.532A8.5 8.5 0 0117.5 8.5V10l1.72-1.72a.75.75 0 011.06 1.06l-3 3a.75.75 0 01-1.06 0l-3-3a.75.75 0 111.06-1.06L16 10V8.5a7 7 0 00-.688-3.812zM2 10l1.72 1.72a.75.75 0 11-1.06 1.06l-3-3a.75.75 0 010-1.06l3-3a.75.75 0 011.06 1.06L2 7.5V8a7 7 0 0012.466 4.418.75.75 0 011.18.928A8.5 8.5 0 010 8v-.5L0 10z"
			clipRule="evenodd"
		/>
	</svg>
);

const MonitorIllustration = () => (
	<div
		className={`${rowThumbnailClass} flex items-center justify-center
			bg-gray-100 text-gray-400`}
		aria-hidden="true"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			className="size-12"
		>
			<path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
		</svg>
	</div>
);

export const TOO_SOON_NOTICE = 'That time is too soon. Pick a later time.';
export const ALREADY_PASSED_NOTICE =
	'That time has already passed. Pick a later time.';

type DateAndTimeFieldsProps = {
	value: ZonedDateTime;
	onChange: (next: ZonedDateTime) => void;
	// How far ahead of now a pick has to be. A post row takes the default, which
	// is the publish-now threshold. An email row passes 0.
	minLeadMs?: number;
	tooSoonNotice?: string;
	// A caller that commits on its own button, rather than on each pick, has to
	// know that the control is showing a pick it will not store. Without this the
	// button writes the last stored value and hides the one on screen.
	onPendingChange?: (isPending: boolean) => void;
};

export const DateAndTimeFields = ({
	value,
	onChange,
	minLeadMs,
	tooSoonNotice = TOO_SOON_NOTICE,
	onPendingChange,
}: DateAndTimeFieldsProps) => {
	const tz = getLocalTimeZone();
	const minDate = useMemo(() => now(tz), [tz]);

	// A pick the panel will not store yet. Typing a time is one digit at a time,
	// and picking a date keeps the time the row already carries, so a customer
	// passes through times that are past or too close to now. Holding the pick
	// here keeps it on screen while it is refused, instead of snapping the fields
	// back to the stored value or replacing the customer's date with a made-up
	// one.
	const valueKey = value.toDate().getTime();
	const [pending, setPending] = usePendingSchedulePick<ZonedDateTime>(
		valueKey,
		onPendingChange,
	);
	const shown = pending ?? value;

	const timeValue = useMemo(
		() => new Time(shown.hour, shown.minute),
		[shown.hour, shown.minute],
	);

	// Email rows pass `minLeadMs={0}`. The server holds a `scheduled_once` send
	// judged on the whole UTC minute (`_handle_activation_triggers` in
	// api/routes/processes.py), not the exact instant, so an instant-only
	// threshold check at leadMs=0 would accept a pick inside the current minute
	// that the server holds anyway — `isSchedulableTime` is instant-based even
	// at leadMs=0. Post rows keep the instant+lead-time rule: Express has no
	// such minute-truncation.
	const isRefused = useCallback(
		(candidate: ZonedDateTime) =>
			minLeadMs === 0
				? isSendMinutePast(candidate)
				: !isSchedulableTime(candidate.toDate(), minLeadMs),
		[minLeadMs],
	);

	const apply = useCallback(
		(merged: ZonedDateTime) => {
			if (isRefused(merged)) {
				setPending(merged);
				return;
			}
			setPending(null);
			onChange(merged);
		},
		[onChange, isRefused, setPending],
	);
	// A stored time can already have gone by, and the owner has to see the real
	// stored value to know what to correct. Showing a different, valid-looking
	// time would hide the problem, and the send is held either way. Read off
	// `shown` (pending pick, or the stored value), so an already-stale stored
	// value is flagged on first render and a freshly-refused pick stays flagged
	// while it sits on screen.
	const isPast = isRefused(shown);

	const handleDateChange = useCallback(
		(picked: ZonedDateTime | null) => {
			if (!picked) return;
			apply(
				picked.set({
					hour: shown.hour,
					minute: shown.minute,
					second: shown.second,
				}),
			);
		},
		[apply, shown],
	);

	const handleTimeChange = useCallback(
		(picked: Time | null) => {
			if (!picked) return;
			apply(
				shown.set({
					hour: picked.hour,
					minute: picked.minute,
					second: 0,
				}),
			);
		},
		[apply, shown],
	);

	return (
		<div className="flex w-full flex-col gap-1">
			<div className="flex w-full items-center gap-2">
				<div className="min-w-0 flex-1">
					<DatePicker
						aria-label="Date"
						granularity="day"
						value={shown}
						minValue={minDate}
						isInvalid={isPast}
						errorMessage={PAST_PICK_MESSAGE}
						onChange={handleDateChange}
						size="M"
						styles={datePickerStyles}
					/>
				</div>
				<div className="min-w-0 flex-1">
					<TimeField
						aria-label="Time"
						hourCycle={12}
						value={timeValue}
						onChange={handleTimeChange}
						size="M"
						styles={datePickerStyles}
					/>
				</div>
			</div>
			{pending ? (
				<p
					className="text-sm text-red-600"
					data-testid="schedule-too-soon-notice"
				>
					{tooSoonNotice}
				</p>
			) : null}
		</div>
	);
};

export const InstagramPostRow = ({
	artifact,
	isStandalone,
	onModeChange,
	onPendingChange,
}: {
	artifact: InstagramPost;
	isStandalone: boolean;
	onModeChange?: (mode: 'now' | 'schedule') => void;
	// The panel commits on its own Schedule button, not on each pick, so it has
	// to know that this row is showing a time the control refused to store.
	onPendingChange?: (isPending: boolean) => void;
}) => {
	const bridgeApi = useBridgeApi();
	const schedule = artifact.schedule ?? null;
	const didDefault = useRef(false);
	const fallbackDateRef = useRef(futureDefaultDate());

	const scheduleKey =
		schedule?.kind === 'absolute' ? `${schedule.on}T${schedule.at}` : null;
	const scheduleZdt = useMemo(() => {
		if (!scheduleKey) return null;
		try {
			return toZoned(parseDateTime(scheduleKey), getLocalTimeZone());
		} catch {
			return null;
		}
	}, [scheduleKey]);

	const derivedMode = useMemo<'schedule' | 'now'>(() => {
		if (isPublishNowSentinel(schedule)) return 'now';
		return scheduleZdt ? 'schedule' : 'now';
	}, [schedule, scheduleZdt]);

	const [optimisticMode, setOptimisticMode] = useState<
		'schedule' | 'now' | null
	>(null);
	const mode = optimisticMode ?? derivedMode;

	useEffect(() => {
		onModeChange?.(mode);
	}, [mode, onModeChange]);

	useEffect(() => {
		if (optimisticMode && optimisticMode === derivedMode) {
			setOptimisticMode(null);
		}
	}, [optimisticMode, derivedMode]);

	const [optimisticValue, setOptimisticValue] = useState<ZonedDateTime | null>(
		null,
	);

	useEffect(() => {
		if (!optimisticValue || !scheduleZdt) return;
		if (optimisticValue.toDate().getTime() === scheduleZdt.toDate().getTime()) {
			setOptimisticValue(null);
		}
	}, [optimisticValue, scheduleZdt]);

	const pickerValue = useMemo(
		() => optimisticValue ?? scheduleZdt ?? fallbackDateRef.current,
		[optimisticValue, scheduleZdt],
	);

	const dispatchSchedule = useCallback(
		(next: InstagramPostSchedule) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: { schedule: next },
			});
		},
		[bridgeApi, artifact.id],
	);

	// A post with no schedule at all blocks the Activate button, so default it
	// to publishing when the campaign activates. This mirrors EmailRow.
	//
	// A legacy row is the same trap with a stored instant behind it. It keeps
	// the whole instant in `fire_at`, which no reader in this panel understands:
	// the row says it publishes on activation, the Activate button stays
	// disabled with no reason on screen, and activation skips the row, so the
	// post never publishes at all. Rewrite the instant once into the shape every
	// reader shares. A past instant then reads as publish-now and is resolved
	// again at activation, which is what the product already does with a stale
	// automatic time.
	useEffect(() => {
		if (didDefault.current) return;
		if (!schedule) {
			didDefault.current = true;
			dispatchSchedule({ kind: 'on_activation' });
			return;
		}
		const legacy = legacyFireAtIso(schedule);
		if (!legacy) return;
		didDefault.current = true;
		dispatchSchedule(absoluteScheduleFrom(new Date(legacy)));
	}, [dispatchSchedule, schedule]);

	const handleChange = useCallback(
		(next: ZonedDateTime) => {
			setOptimisticValue(next);
			fallbackDateRef.current = next;
			dispatchSchedule(absoluteScheduleFrom(next.toDate()));
		},
		[dispatchSchedule],
	);

	const handleModeChange = useCallback(
		(next: string) => {
			if (next !== 'schedule' && next !== 'now') return;
			setOptimisticMode(next);
			if (next === 'schedule') {
				const target = futureDefaultDate();
				fallbackDateRef.current = target;
				dispatchSchedule(absoluteScheduleFrom(target.toDate()));
			} else {
				dispatchSchedule({ kind: 'on_activation' });
			}
		},
		[dispatchSchedule],
	);

	const title = scheduleItemTitle(artifact);
	const thumb = artifact.content.image_urls?.[0];

	return (
		<div
			className="flex items-stretch gap-4 py-4"
			data-testid="schedule-post-row"
			data-artifact-id={artifact.id}
		>
			{thumb ? (
				<BlobImage
					src={thumb}
					alt=""
					className={`${rowThumbnailClass} object-cover`}
				/>
			) : (
				<div className={`${rowThumbnailClass} bg-gray-100`} />
			)}
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex min-h-12 items-center">
					{mode === 'schedule' ? (
						<DateAndTimeFields
							value={pickerValue}
							onChange={handleChange}
							onPendingChange={onPendingChange}
						/>
					) : (
						<div className="flex flex-wrap items-center gap-2">
							<CalendarGlyph muted />
							<span className="text-base text-gray-700">
								{isStandalone
									? 'Publishes when you click Publish'
									: 'Publishes when the campaign activates'}
							</span>
						</div>
					)}
				</div>
				<Checkbox
					isSelected={mode === 'now'}
					onChange={(checked) => handleModeChange(checked ? 'now' : 'schedule')}
				>
					Publish now
				</Checkbox>
			</div>
		</div>
	);
};

export const EmailRow = ({
	action,
	artifact,
	isStandalone,
	onPendingChange,
}: {
	action: ProcessAction;
	artifact: Artifact;
	isStandalone: boolean;
	// See `InstagramPostRow`: the panel's own button commits, so it has to know
	// this row is showing a time the control refused to store.
	onPendingChange?: (isPending: boolean) => void;
}) => {
	const bridgeApi = useBridgeApi();
	const trigger = action.trigger ?? null;
	const didDefault = useRef(false);

	const fallbackDateRef = useRef(futureDefaultDate());
	const [optimisticValue, setOptimisticValue] = useState<ZonedDateTime | null>(
		null,
	);

	const triggerKey =
		trigger?.type === 'scheduled_once' ? trigger.timestamp : null;
	const triggerZdt = useMemo(
		() => parseStoredTriggerTime(triggerKey),
		[triggerKey],
	);

	useEffect(() => {
		if (!optimisticValue || !triggerZdt) return;
		if (optimisticValue.toDate().getTime() === triggerZdt.toDate().getTime()) {
			setOptimisticValue(null);
		}
	}, [optimisticValue, triggerZdt]);

	const pickerValue = useMemo(
		() => optimisticValue ?? triggerZdt ?? fallbackDateRef.current,
		[optimisticValue, triggerZdt],
	);

	const dispatchTrigger = useCallback(
		(next: TriggerSpec) => {
			bridgeApi.sendAction({
				type: 'do_edit_process_action',
				action_id: action.id,
				updates: { trigger: next },
			});
		},
		[bridgeApi, action.id],
	);

	useEffect(() => {
		if (didDefault.current) return;
		if (trigger) return;
		didDefault.current = true;
		dispatchTrigger({ type: 'on_activation' });
	}, [dispatchTrigger, trigger]);

	const handleChange = useCallback(
		(next: ZonedDateTime) => {
			setOptimisticValue(next);
			fallbackDateRef.current = next;
			dispatchTrigger({
				type: 'scheduled_once',
				timestamp: normalizeToUtcIsoZ(next.toDate().toISOString()),
			});
		},
		[dispatchTrigger],
	);

	const handleModeChange = useCallback(
		(next: string) => {
			if (next === 'schedule') {
				const target = futureDefaultDate();
				fallbackDateRef.current = target;
				dispatchTrigger({
					type: 'scheduled_once',
					timestamp: normalizeToUtcIsoZ(target.toDate().toISOString()),
				});
			} else {
				dispatchTrigger({ type: 'on_activation' });
			}
		},
		[dispatchTrigger],
	);

	const mode: 'schedule' | 'now' =
		trigger?.type === 'on_activation' ? 'now' : 'schedule';
	const title = scheduleItemTitle(artifact);

	return (
		<div className="flex items-stretch gap-4 py-4">
			<EmailThumbnail artifact={artifact} />
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<p className="line-clamp-1 text-lg font-bold text-gray-900">{title}</p>
				<div className="flex min-h-12 items-center">
					{mode === 'schedule' ? (
						<DateAndTimeFields
							value={pickerValue}
							onChange={handleChange}
							minLeadMs={0}
							tooSoonNotice={ALREADY_PASSED_NOTICE}
							onPendingChange={onPendingChange}
						/>
					) : (
						<div className="flex flex-wrap items-center gap-2">
							<CalendarGlyph muted />
							<span className="text-base text-gray-700">
								{isStandalone
									? 'Sends when you click Send'
									: 'Sends when the campaign activates'}
							</span>
						</div>
					)}
				</div>
				<Checkbox
					isSelected={mode === 'now'}
					onChange={(checked) => handleModeChange(checked ? 'now' : 'schedule')}
				>
					Send now
				</Checkbox>
			</div>
		</div>
	);
};

export const MonitorRow = () => {
	return (
		<div className="flex items-center gap-4 py-4">
			<MonitorIllustration />
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<p className="text-lg font-bold text-gray-900">Automated DMs</p>
				<div className="flex items-center gap-2">
					<RefreshGlyph />
					<span className="text-base text-gray-500">Ongoing</span>
				</div>
			</div>
		</div>
	);
};

// True when starting the campaign will NOT send this email, and the owner has to
// confirm it first.
//
// Two triggers are held. A one-time send whose time has already gone by, and an
// `on_activation` send when the press carries no `send_immediately` — which only
// `email_send` does, so a campaign's `on_activation` email waits too. A
// timestamp that no reading can make sense of is dropped before dispatch, so it
// is no promise to correct here.
function isHeldEmailAction(
	action: ProcessAction,
	sendImmediately: boolean,
): boolean {
	const trigger = action.trigger;
	if (trigger?.type === 'on_activation') return !sendImmediately;
	if (trigger?.type !== 'scheduled_once') return false;
	const at = parseStoredTriggerTime(trigger.timestamp);
	return at !== null && isSendMinutePast(at);
}

function summarizeSchedule(
	postArtifacts: InstagramPost[],
	emailActions: ProcessAction[],
	hasMonitor: boolean,
	heldEmails: ReadonlySet<ProcessAction>,
): string {
	const parts: string[] = [];
	if (postArtifacts.length > 0) {
		const publishingNow = postArtifacts.filter(
			(p) => p.schedule?.kind === 'on_activation',
		).length;
		const scheduled = postArtifacts.filter(
			(p) => p.schedule?.kind === 'absolute' && p.schedule.on && p.schedule.at,
		).length;
		if (publishingNow > 0) {
			const segs: string[] = [
				`${publishingNow} post${publishingNow === 1 ? '' : 's'} publishing now`,
			];
			if (scheduled > 0) segs.push(`${scheduled} scheduled`);
			parts.push(segs.join(', '));
		} else if (scheduled === postArtifacts.length) {
			parts.push(
				postArtifacts.length === 1
					? 'Post scheduled'
					: `${postArtifacts.length} posts scheduled`,
			);
		} else {
			parts.push(postArtifacts.length === 1 ? 'Post ready' : 'Posts ready');
		}
	}
	if (emailActions.length > 0) {
		// Every email lands in exactly one bucket. A held email is neither sending
		// nor scheduled — it waits on the owner — so counting it in either of the
		// other two promises a send that is not coming.
		const heldEmailCount = heldEmails.size;
		const sendNow = emailActions.filter(
			(a) => a.trigger?.type === 'on_activation' && !heldEmails.has(a),
		).length;
		const scheduled = emailActions.filter(
			(a) => a.trigger?.type === 'scheduled_once' && !heldEmails.has(a),
		).length;
		const segs: string[] = [];
		if (sendNow > 0)
			segs.push(`${sendNow} email${sendNow === 1 ? '' : 's'} sending now`);
		if (scheduled > 0) segs.push(`${scheduled} scheduled`);
		if (heldEmailCount > 0) segs.push(`${heldEmailCount} waiting for your OK`);
		if (segs.length > 0) parts.push(segs.join(', '));
	}
	if (hasMonitor) parts.push('Monitoring on');
	if (parts.length === 0) return 'Your campaign is now active.';
	return parts.join(' · ');
}

function rowHasSchedule(
	artifact: Artifact,
	emailActionsByRef: Map<string, ProcessAction>,
): boolean {
	if (artifact.type === 'instagram_post') {
		const sched = (artifact as InstagramPost).schedule;
		if (!sched) return false;
		if (sched.kind === 'on_activation') return true;
		return !!(sched.kind === 'absolute' && sched.on && sched.at);
	}
	if (!artifact.ref) return false;
	const action = emailActionsByRef.get(artifact.ref);
	return action?.trigger != null;
}

// The last step of a campaign whose artifacts are all monitors. Each monitor
// already carries its own cadence, or it runs from the Instagram webhook, so
// there is no date to pick. This lists what is about to start watching.
//
// The campaign can still carry the owner's own steps. An ask is an ACTION with
// no artifact, so it never appears among the children the monitor test reads,
// and a campaign of monitors plus an ask still reaches this body. The server
// activates those asks, so leaving them off the list would start work the owner
// was never shown.
const NoDateReviewBody = ({
	monitors,
	alwaysOn,
	asks,
}: {
	monitors: Artifact[];
	alwaysOn: Artifact[];
	asks: ProcessAction[];
}) => (
	<div className="flex min-w-0 flex-col gap-4 p-4">
		{monitors.length > 0 && (
			<>
				<p className="text-sm text-(--text-primary)">
					These monitors run on their own schedule, so there is nothing to date.
					Start them when you are ready, and we will tell you what they find.
				</p>
				<ul
					data-testid="monitor-only-review-list"
					className="flex list-disc flex-col gap-3 pl-5 text-sm
						text-(--text-primary)"
				>
					{monitors.map((monitor) => (
						<li key={monitor.id} className="pl-1">
							{monitor.user_summary || TYPE_TAGS[monitor.type] || 'Job'}
						</li>
					))}
				</ul>
			</>
		)}
		{alwaysOn.length > 0 && (
			<>
				<p className="text-sm text-(--text-primary)">
					{monitors.length > 0
						? 'This goes live as soon as you start, and it stays on.'
						: 'These go live as soon as you start, and they stay on. There is no date to pick.'}
				</p>
				<ul
					data-testid="monitor-review-always-on-list"
					className="flex list-disc flex-col gap-3 pl-5 text-sm
						text-(--text-primary)"
				>
					{alwaysOn.map((artifact) => (
						<li key={artifact.id} className="pl-1">
							{artifact.user_summary || TYPE_TAGS[artifact.type] || 'Job'}
						</li>
					))}
				</ul>
			</>
		)}
		{asks.length > 0 && (
			<>
				<p className="text-sm text-(--text-primary)">
					These steps are yours to do. We will ask you how each one went.
				</p>
				<ol
					data-testid="monitor-review-owner-list"
					className="flex list-decimal flex-col gap-3 pl-5 text-sm
						text-(--text-primary)"
				>
					{asks.map((ask) => (
						<li key={ask.id} className="pl-1">
							{(ask.config as RequestOwnerActionConfig)?.instruction ?? ''}
						</li>
					))}
				</ol>
			</>
		)}
	</div>
);

// The last step of a campaign the owner runs themselves. There is no date to
// pick, so this restates what they agreed to do and lets them start.
const OwnerOnlyReviewBody = ({ asks }: { asks: ProcessAction[] }) => (
	<div className="flex min-w-0 flex-col gap-4 p-4">
		<p className="text-sm text-(--text-primary)">
			These steps are yours to do. Bridgeway sends nothing and posts nothing for
			this campaign. Start it when you are ready, and we will ask you how each
			step went.
		</p>
		<ol
			data-testid="owner-only-review-list"
			className="flex list-decimal flex-col gap-3 pl-5 text-sm
				text-(--text-primary)"
		>
			{asks.map((ask) => (
				<li key={ask.id} className="pl-1">
					{(ask.config as RequestOwnerActionConfig)?.instruction ?? ''}
				</li>
			))}
		</ol>
	</div>
);

export const ProcessSchedulePanel = ({
	processId,
	onBack,
	onClose,
	style,
}: ProcessSchedulePanelProps) => {
	const bridgeApi = useBridgeApi();
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { expressApi, hasValidInstagramConnection, beginExpressConnection } =
		useExpressApiContext();
	const { settings } = useUserSettings();
	const openChatPanel = useOpenChatPanel();
	useEnsureSetupSnapshot(processId);
	const snapshot =
		bridgeApi.user_state?.process_setup_snapshots?.[processId] ?? null;
	const setupError =
		bridgeApi.user_state?.process_setup_errors?.[processId] ?? null;

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const currentChat = currentChatId
		? (bridgeApi.user_state?.chats?.[currentChatId] ?? null)
		: null;
	const linkedArtifactId =
		currentChat?.linked_item?.type === 'artifact'
			? currentChat.linked_item.id
			: null;
	const linkedChild = linkedArtifactId
		? (snapshot?.children.find((c) => c.id === linkedArtifactId) ?? null)
		: null;

	const [interstitial, setInterstitial] = useState<{
		title: string;
		summary: string;
	} | null>(null);
	const [isActivating, setIsActivating] = useState(false);
	const [mutationError, setMutationError] = useState<string | null>(null);
	// Holds the already-committed time, formatted, while the panel asks whether
	// the owner really means to schedule the same thing again.
	const [duplicateConfirm, setDuplicateConfirm] = useState<string | null>(null);
	const [igToggleMode, setIgToggleMode] = useState<'now' | 'schedule' | null>(
		null,
	);

	const schedulableChildren = useMemo<Artifact[]>(() => {
		if (!snapshot) return [];
		const all = snapshot.children.filter(
			(c) =>
				SCHEDULABLE_ARTIFACT_TYPES.has(c.type) && !isSkipped(snapshot, c.id),
		);
		if (linkedChild) return all.filter((c) => c.id === linkedChild.id);
		return all;
	}, [snapshot, linkedChild]);

	const emailActionsByRef = useMemo(() => {
		const map = new Map<string, ProcessAction>();
		if (!snapshot) return map;
		for (const action of snapshot.actions ?? []) {
			if (action.type !== 'send_email_to_contacts') continue;
			const ref = (action.config as { artifact_ref?: string })?.artifact_ref;
			if (ref) map.set(ref, action);
		}
		return map;
	}, [snapshot]);

	const monitorActions = useMemo<ProcessAction[]>(() => {
		if (!snapshot) return [];
		return (snapshot.actions ?? []).filter(
			(a) => a.type === 'monitor_instagram_comments_and_reply',
		);
	}, [snapshot]);

	const ownerAsks = useMemo<ProcessAction[]>(() => {
		if (!snapshot) return [];
		const skipped = new Set(snapshot.skipped_action_ids ?? []);
		return (snapshot.actions ?? []).filter(
			(a) => a.type === 'request_owner_action' && !skipped.has(a.id),
		);
	}, [snapshot]);

	const postArtifacts = useMemo(
		() => schedulableChildren.filter((c) => c.type === 'instagram_post'),
		[schedulableChildren],
	);
	const emailArtifacts = useMemo(
		() =>
			schedulableChildren.filter(
				(c) => c.type === 'email_html' || c.type === 'email_plaintext',
			),
		[schedulableChildren],
	);

	// Editing any post's date is a new decision, so the panel stops asking about
	// a repeat and the button goes back to its normal label.
	const postScheduleSignature = useMemo(
		() =>
			(postArtifacts as InstagramPost[])
				.map((p) => `${p.id}:${JSON.stringify(p.schedule ?? null)}`)
				.join('|'),
		[postArtifacts],
	);
	useEffect(() => {
		setDuplicateConfirm(null);
	}, [postScheduleSignature]);

	// An owner-only campaign has nothing to schedule, so "every item has a date"
	// is vacuously true rather than a failure. Reading it as unscheduled left the
	// start button permanently disabled, and the campaign could never begin. A
	// monitors-only campaign is the same trap: each monitor holds its own
	// cadence, or it runs from the Instagram webhook, so there is no date to pick.
	const isOwnerOnly = snapshot ? isOwnerOnlyProcess(snapshot) : false;
	const isMonitorOnly = snapshot ? isMonitorOnlyProcess(snapshot) : false;
	// A campaign of only always-on artifacts (ads). It needs no date, and unlike
	// a sole monitor no artifact panel here offers its own start, so this row is
	// the only control that can start it.
	const isAlwaysOnOnly = snapshot ? isAlwaysOnOnlyProcess(snapshot) : false;
	// A single derived mode, resolved once, so the title/label/body ternaries
	// below all pick the same branch instead of each re-deriving the same
	// ownerOnly-wins-if-both order. isOwnerOnly and isMonitorOnly can't both be
	// true by construction (isOwnerOnlyProcess requires zero surviving children;
	// isMonitorOnlyProcess requires at least one), but resolving that here once
	// means a future third mode is one switch arm away, not a sixth ternary.
	const reviewMode: 'ownerOnly' | 'monitorOnly' | 'alwaysOnOnly' | 'normal' =
		isOwnerOnly
			? 'ownerOnly'
			: isMonitorOnly
				? 'monitorOnly'
				: isAlwaysOnOnly
					? 'alwaysOnOnly'
					: 'normal';
	const allScheduled =
		reviewMode !== 'normal' ||
		(schedulableChildren.length > 0 &&
			schedulableChildren.every((c) => rowHasSchedule(c, emailActionsByRef)));

	// Rows showing a pick the control refused to store. `allScheduled` cannot
	// answer this: `rowHasSchedule` reads the STORED schedule, and a refused pick
	// is never stored, so every row still counts as scheduled while a refused
	// time sits on screen. Activation would then send the old time to Express and
	// leave the customer looking at the one they picked.
	const [pendingRowIds, setPendingRowIds] = useState<ReadonlySet<string>>(
		new Set(),
	);
	// Returning `prev` unchanged when nothing moved is load-bearing. A new Set on
	// every report re-renders the panel, which re-runs the rows' effects, which
	// report again. That loop killed the test worker once already.
	const setRowPending = useCallback((id: string, isPending: boolean) => {
		setPendingRowIds((prev) => {
			if (prev.has(id) === isPending) return prev;
			const next = new Set(prev);
			if (isPending) next.add(id);
			else next.delete(id);
			return next;
		});
	}, []);
	const hasPendingPick = pendingRowIds.size > 0;

	const needsExpressConnect =
		postArtifacts.length > 0 && (!expressApi || !hasValidInstagramConnection);

	// Express posts this panel has already created, by artifact id. An activation
	// that fails part way records nothing in Cosmos and nothing in the snapshot,
	// so without this a retry would create a second Express post for the same
	// artifact and publish the same caption twice.
	const committedExpressRef = useRef<Map<string, ExpressMetadata>>(new Map());

	const previousHasValidInstagramConnectionRef = useRef(
		hasValidInstagramConnection,
	);
	useEffect(() => {
		const hasJustConnected =
			!previousHasValidInstagramConnectionRef.current &&
			hasValidInstagramConnection;
		if (hasJustConnected) {
			setMutationError(null);
		}
		previousHasValidInstagramConnectionRef.current =
			hasValidInstagramConnection;
	}, [hasValidInstagramConnection]);

	// Skipped artifacts don't activate, so a campaign whose only remaining
	// schedulable item is one post is effectively standalone — count the
	// non-skipped children so the panel shows single-item Publish/Schedule UX
	// instead of the multi-item "Activate campaign" labels.
	const nonSkippedChildren = useMemo(
		() => (snapshot?.children ?? []).filter((c) => !isSkipped(snapshot!, c.id)),
		[snapshot],
	);
	// A monitor is an ARTIFACT that registers its own runtime, so most monitors
	// carry no companion action. `monitorActions` holds one action type, which
	// only the Instagram comment watcher produces, so counting it alone reported
	// no monitor at all for every subreddit and custom monitor campaign.
	const hasMonitor = useMemo(
		() =>
			monitorActions.length > 0 ||
			nonSkippedChildren.some((c) => MONITOR_ARTIFACT_TYPES.has(c.type)),
		[monitorActions, nonSkippedChildren],
	);
	// An always-on artifact reaches the monitors-only review because it needs no
	// date, not because it watches anything. The two lists stay apart so the
	// review never calls a paid ad a monitor.
	const monitorChildren = useMemo(
		() => nonSkippedChildren.filter((c) => MONITOR_ARTIFACT_TYPES.has(c.type)),
		[nonSkippedChildren],
	);
	const alwaysOnChildren = useMemo(
		() =>
			nonSkippedChildren.filter((c) => ALWAYS_ON_ARTIFACT_TYPES.has(c.type)),
		[nonSkippedChildren],
	);

	// A monitors-only campaign is never standalone via the surviving count, even
	// when a skip leaves exactly one child behind: reviewMode is keyed off the
	// RAW count precisely so a skip can't flip it, and this branch disagreeing
	// would show the single-artifact Back button (into that monitor's own
	// editor) inside what is otherwise the monitors-only start review. A
	// `linkedChild` deep link is a distinct, deliberate navigation and stays
	// standalone regardless.
	const isMonitorOnlyReview = reviewMode === 'monitorOnly';
	const isStandalone =
		!!linkedChild || (!isMonitorOnlyReview && nonSkippedChildren.length === 1);
	const standaloneChild =
		linkedChild ??
		(!isMonitorOnlyReview && nonSkippedChildren.length === 1
			? (nonSkippedChildren[0] ?? null)
			: null);

	const activateMode = useMemo<ActivateMode>(() => {
		if (!isStandalone || !standaloneChild) return 'campaign';
		if (standaloneChild.type === 'instagram_post') {
			return igToggleMode === 'schedule' ? 'ig_schedule' : 'ig_publish';
		}
		if (
			standaloneChild.type === 'email_html' ||
			standaloneChild.type === 'email_plaintext'
		) {
			const action = standaloneChild.ref
				? emailActionsByRef.get(standaloneChild.ref)
				: undefined;
			const triggerType = action?.trigger?.type;
			if (triggerType === 'scheduled_once') return 'email_schedule';
			// `email_send` is the owner's own "Send now", and only `on_activation`
			// records that choice. A drip step and a cadence fire on their own
			// terms: the server handles neither at activation, so this press does
			// not send them and does not hold them either. Reading every other kind
			// as a send titled the press "Sent" and wrote "Sending now — your email
			// is on its way" for mail that was not going out. A row with no trigger
			// yet is about to become `on_activation`, so it keeps the send mode.
			if (triggerType && triggerType !== 'on_activation') return 'campaign';
			return 'email_send';
		}
		return 'campaign';
	}, [emailActionsByRef, igToggleMode, isStandalone, standaloneChild]);

	const activateEventName = {
		campaign: 'Campaign Activated',
		ig_publish: 'Instagram Post Published',
		ig_schedule: 'Instagram Post Scheduled',
		email_send: 'Email Sent',
		email_schedule: 'Email Scheduled',
	}[activateMode];

	const handleActivate = useCallback(
		async (force = false) => {
			if (!snapshot || !userId) return;

			// Reopening this panel is a fresh mount, so nothing held in a ref can see
			// the schedule the last mount committed. Read the persisted target
			// instead: when every schedulable post is already scheduled for the same
			// minute it is going to now, this press repeats work that is already
			// done. It would re-upload every slide and post a second confirmation to
			// the chat. Ask before repeating it, so a deliberate reschedule still
			// works.
			if (!force && postArtifacts.length > 0) {
				const targets = (postArtifacts as InstagramPost[]).map((artifact) => {
					const sched = artifact.schedule;
					if (!sched) return null;
					const committedTarget = artifact.express_metadata?.publish_date;
					if (artifact.status !== 'scheduled' || !committedTarget) return null;
					// `on_activation` has no stored target to compare against — the
					// committed schedule fires whenever THIS press's activation lands,
					// so re-resolving "now" would almost never match the earlier
					// commit even on a genuine repeat. Being already scheduled at all
					// is itself the duplicate signal for this schedule kind.
					if (isPublishNowSentinel(sched)) return committedTarget;
					const target = absolutePublishDateIso(sched);
					if (!target) return null;
					return isSameMinute(committedTarget, target) ? target : null;
				});
				// One changed row means there is real work to do, so let it through.
				const everyRowIsDuplicate =
					targets.length > 0 && targets.every((t) => t !== null);
				if (everyRowIsDuplicate) {
					setDuplicateConfirm(formatScheduleDate(targets[0] as string));
					return;
				}
			}

			setDuplicateConfirm(null);
			setMutationError(null);
			setIsActivating(true);

			try {
				const channelInfo = expressApi
					? getInstagramChannelFromCalendars(
							settings?.express_connection?.calendar,
						)
					: null;

				// Express ids captured here are mirrored into the snapshot via
				// do_activate_process (in-memory only) so a same-session reschedule
				// reuses the Express post instead of creating a duplicate.
				const scheduled: Array<{
					artifact_id: string;
					express_metadata: {
						post_id: string;
						attachment_ids: string[];
						image_urls_at_upload: string[];
					};
					schedule: InstagramPostSchedule;
					status: 'scheduled';
				}> = [];

				// Pass one uploads every slide and commits nothing. The upload is the
				// slow, failure-prone step. Express publishes a post about a minute
				// after it accepts it, and nothing cancels a post Express already
				// holds, so a post must not reach Express while a later upload can
				// still fail.
				const uploaded: Array<{
					artifact: InstagramPost;
					sched: InstagramPostSchedule;
					mediaIds: string[];
				}> = [];

				for (const artifact of postArtifacts as InstagramPost[]) {
					const sched = artifact.schedule;
					if (!sched) continue;
					// A row that is neither a publish-now row nor a readable date, such
					// as a legacy fire_at row or one holding empty on and at fields, has
					// no date to send, so it stays out of this activation. Both passes
					// skip the same rows: a row fails both tests only when its stored
					// date will not parse, and parsing reads no clock.
					if (!isPublishNowSentinel(sched) && !absolutePublishDateIso(sched)) {
						continue;
					}

					if (!expressApi || !channelInfo) {
						throw new Error('Missing Express connection details');
					}

					// An earlier attempt may already hold this post's attachment ids.
					// Passing them in reuses the upload for every slide whose url is
					// unchanged.
					const carried = committedExpressRef.current.get(artifact.id);
					const mediaIds = await uploadSlidesToExpress({
						expressApi,
						channelInfo,
						userId,
						artifact: carried
							? ({ ...artifact, express_metadata: carried } as InstagramPost)
							: artifact,
					});
					uploaded.push({ artifact, sched, mediaIds });
				}

				// Pass two commits each post. It reads the clock inside the iteration,
				// with no await between the read and that post's own Express call, so
				// the date cannot expire on the way. Express refuses a past date.
				for (const { artifact, sched, mediaIds } of uploaded) {
					if (!expressApi || !channelInfo) {
						throw new Error('Missing Express connection details');
					}

					const resolveFresh = isPublishNowSentinel(sched);
					const storedPublishDate = resolveFresh
						? null
						: absolutePublishDateIso(sched);

					let publishDate: string;
					let resolved: InstagramPostSchedule;
					if (storedPublishDate) {
						publishDate = storedPublishDate;
						resolved = sched;
					} else {
						publishDate = resolveOnActivationPublishDate();
						resolved = absoluteScheduleFrom(new Date(publishDate));
					}

					let expressPostId =
						committedExpressRef.current.get(artifact.id)?.post_id ??
						artifact.express_metadata?.post_id;
					if (expressPostId) {
						await expressApi.updatePost(expressPostId, {
							caption: artifact.content.caption,
							channelId: channelInfo.channelId,
							attachmentIds: mediaIds,
							publishDate,
							calendarId: channelInfo.calendarId,
							publishType: 'schedule',
						});
					} else {
						const created = await expressApi.createPost({
							calendarId: channelInfo.calendarId,
							caption: artifact.content.caption,
							channelId: channelInfo.channelId,
							mediaIds,
							publishDate,
							publishType: 'schedule',
						});
						expressPostId = created[0]?.id;
					}

					// Record the id before the patch. A rejected patch would otherwise
					// leave it in no store at all, and the next press would create a
					// second Express post for this artifact. One object serves all three
					// stores, so they cannot drift.
					const committed = expressPostId
						? {
								post_id: expressPostId,
								attachment_ids: mediaIds,
								image_urls_at_upload: artifact.content.image_urls ?? [],
								// The target this activation actually sent to Express. Editing a
								// row's date changes `schedule` but leaves this at the last
								// committed value, which is how the duplicate guard tells a real
								// reschedule apart from a repeat of the same schedule.
								publish_date: publishDate,
							}
						: undefined;
					if (committed) {
						committedExpressRef.current.set(artifact.id, committed);
					}

					await bridgewayApi.patch(
						`/api/user/${userId}/artifacts/${artifact.id}`,
						{
							status: 'scheduled',
							schedule: resolved,
							scheduled_at: new Date().toISOString(),
							express_metadata: committed,
						},
					);

					if (committed) {
						scheduled.push({
							artifact_id: artifact.id,
							express_metadata: committed,
							schedule: resolved,
							status: 'scheduled',
						});
					}
				}

				const emailActions = emailArtifacts
					.map((e) => (e.ref ? emailActionsByRef.get(e.ref) : undefined))
					.filter((a): a is ProcessAction => !!a);
				// A prediction of what the server will hold, read off the browser
				// clock. It only picks wording, and the notice that follows the
				// activation corrects it from the server's own answer, so a clock
				// that is a few seconds out costs nothing.
				// Only `email_send` carries the owner's yes to the server, so every
				// other mode leaves an `on_activation` email waiting.
				const sendImmediately = activateMode === 'email_send';
				const heldEmails = new Set(
					emailActions.filter((a) => isHeldEmailAction(a, sendImmediately)),
				);
				const heldEmailCount = heldEmails.size;
				const summary = summarizeSchedule(
					postArtifacts as InstagramPost[],
					emailActions,
					hasMonitor,
					heldEmails,
				);
				trackEvent(activateEventName, {
					process_id: processId,
					post_count: postArtifacts.length,
					email_count: emailArtifacts.length,
					has_monitor: hasMonitor,
				});
				const scheduledIso =
					activateMode === 'ig_schedule' &&
					standaloneChild?.type === 'instagram_post'
						? postScheduleIso(standaloneChild as InstagramPost)
						: null;
				// A campaign that needs no date publishes nothing and sends nothing, so
				// the generic activation wording would promise work that is not coming.
				// It can hold monitors, always-on artifacts and the owner's own asks in
				// any combination, and NoDateReviewBody lists whichever it has. The
				// success message names each one it actually started — otherwise the
				// owner reads "you're all set" and never learns that their ad went
				// live, or that a manual step is still theirs to do.
				const noDateSummary = [
					...(monitorChildren.length > 0
						? ["Your monitors are on — I'll tell you what I find."]
						: []),
					...(alwaysOnChildren.length > 0
						? [
								alwaysOnChildren.length === 1
									? monitorChildren.length > 0
										? 'Your ad is live too.'
										: 'Your ad is live.'
									: monitorChildren.length > 0
										? 'Your ads are live too.'
										: 'Your ads are live.',
							]
						: []),
					...(ownerAsks.length > 0
						? [
								'You still have your own steps to do, and we will ask you how each one went.',
							]
						: []),
				].join(' ');
				setInterstitial(
					reviewMode === 'monitorOnly'
						? { title: 'Monitoring started', summary: noDateSummary }
						: reviewMode === 'alwaysOnOnly'
							? { title: 'Campaign started', summary: noDateSummary }
							: {
									// "Scheduled"/"Campaign activated" is a false promise for
									// any activation the server is holding an email inside of,
									// not just a lone email. A multi-artifact campaign holding
									// one past-dated email needs the same title a standalone
									// held email gets — the summary below already carries the
									// hold either way.
									title:
										heldEmailCount > 0
											? 'Waiting for your OK'
											: activateSuccessTitle(activateMode),
									summary: activateSuccessSummary(
										activateMode,
										summary,
										scheduledIso,
									),
								},
				);
				let scheduledFor: string | null = scheduledIso
					? formatScheduleDate(scheduledIso)
					: null;
				if (activateMode === 'email_schedule' && standaloneChild?.ref) {
					const trigger = emailActionsByRef.get(standaloneChild.ref)?.trigger;
					// Read the stored send time the way `isHeldEmailAction` above reads
					// it, and the way the server reads it. Formatted with
					// `formatScheduleDate` alone, a zoneless send time was read as
					// local wall time, so this press promised the transcript a send
					// the owner's own UTC offset away from the instant the server
					// dispatches — and the wrong day near midnight.
					scheduledFor =
						trigger?.type === 'scheduled_once'
							? formatTriggerSendTime(trigger.timestamp) || null
							: null;
				}
				bridgeApi.sendAction({
					type: 'do_activate_process',
					process_id: processId,
					scheduled,
					mode: activateMode,
					...(scheduledFor ? { scheduled_for: scheduledFor } : {}),
					...(heldEmailCount > 0 ? { held_email_count: heldEmailCount } : {}),
					// This panel already asked "Send now" or "Schedule". `email_send` IS the owner
					// choosing now, so it carries the yes and Bridgeway does not ask twice. Every
					// other mode leaves the send held.
					...(activateMode === 'email_send' ? { send_immediately: true } : {}),
				});
			} catch (err) {
				setMutationError(
					err instanceof Error
						? err.message
						: 'Failed to schedule posts. Please try again.',
				);
				logger.error(
					'Campaign activation failed during Express scheduling',
					err,
				);
			} finally {
				setIsActivating(false);
			}
		},
		[
			bridgeApi,
			bridgewayApi,
			expressApi,
			settings,
			userId,
			processId,
			snapshot,
			emailArtifacts,
			emailActionsByRef,
			postArtifacts,
			hasMonitor,
			monitorChildren,
			alwaysOnChildren,
			activateEventName,
			activateMode,
			reviewMode,
			ownerAsks,
			standaloneChild,
		],
	);

	const handleViewCampaign = useCallback(() => {
		trackEvent('Campaign View Clicked', { process_id: processId });
		openChatPanel({
			panel_type: 'artifact',
			artifact_type: 'campaign',
			item_id: processId,
		});
	}, [openChatPanel, processId]);

	const handleRetryLoad = useCallback(() => {
		bridgeApi.sendAction({
			type: 'do_load_setup_snapshot',
			process_id: processId,
		});
	}, [bridgeApi, processId]);

	// "Start campaign" when the work is the owner's own, and "Start monitoring"
	// when it is all monitors: nothing is scheduled either way, so the button
	// starts the campaign rather than activating a set of sends. "Start
	// monitoring" is the label the monitor conversations already promise.
	const activateLabel =
		reviewMode === 'ownerOnly'
			? 'Start campaign'
			: reviewMode === 'monitorOnly'
				? 'Start monitoring'
				: reviewMode === 'alwaysOnOnly'
					? 'Start campaign'
					: {
							campaign: 'Activate campaign',
							ig_publish: 'Publish',
							ig_schedule: 'Schedule',
							email_send: 'Send',
							email_schedule: 'Schedule',
						}[activateMode];

	const handleBack = useCallback(() => {
		if (!standaloneChild) return;
		openChatPanel({
			panel_type: 'artifact',
			artifact_type: standaloneChild.type,
			item_id: standaloneChild.id,
		});
	}, [openChatPanel, standaloneChild]);

	const actionRenderer = useCallback(
		() => (
			<div className="ml-auto flex items-center gap-3">
				{mutationError && (
					<span className="text-sm text-red-700">{mutationError}</span>
				)}
				{duplicateConfirm && !mutationError && (
					<span className="text-sm text-gray-700">
						{`Already scheduled for ${duplicateConfirm}. Schedule it again?`}
					</span>
				)}
				{!mutationError && hasPendingPick && (
					<span
						className="text-sm text-gray-700"
						data-testid="pending-pick-hint"
					>
						Fix the time above to continue.
					</span>
				)}
				{isStandalone && (
					<Button
						type="button"
						variant="secondary"
						onPress={handleBack}
						isDisabled={isActivating}
					>
						Back
					</Button>
				)}
				{needsExpressConnect ? (
					<Button
						type="button"
						variant="accent"
						onPress={beginExpressConnection}
						isDisabled={isActivating}
					>
						Connect Express
					</Button>
				) : (
					<Button
						type="button"
						variant="accent"
						onPress={() => void handleActivate(duplicateConfirm !== null)}
						isPending={isActivating}
						isDisabled={!allScheduled || hasPendingPick || isActivating}
					>
						{duplicateConfirm ? 'Schedule again' : activateLabel}
					</Button>
				)}
			</div>
		),
		[
			activateLabel,
			allScheduled,
			beginExpressConnection,
			duplicateConfirm,
			handleActivate,
			handleBack,
			hasPendingPick,
			isActivating,
			isStandalone,
			mutationError,
			needsExpressConnect,
		],
	);

	// The load failed, so the snapshot will never arrive on its own. Without this
	// the panel shows its loading spinner for as long as the customer leaves it
	// open, and nothing tells them the campaign could not be read.
	if (!snapshot && setupError) {
		return (
			<ChatPanelLayout
				title="Schedule"
				isLoading={false}
				onBack={onBack}
				onClose={onClose}
				style={style}
			>
				<div
					className="my-2 rounded-lg border border-red-200 bg-red-50 p-4
						shadow-sm"
				>
					<p className="text-sm font-medium text-red-700">
						Couldn’t load campaign setup
					</p>
					{/* The stored message can carry a raw backend exception, and
					RestApi builds it from the response body. The detail is already in
					the bridge logger.error, so the customer gets plain language. */}
					<p className="mt-1 text-xs text-red-600">
						Your campaign is safe. We could not read its setup just now. Contact
						support if this keeps happening.
					</p>
					<div className="mt-3">
						<Button type="button" variant="secondary" onPress={handleRetryLoad}>
							Try again
						</Button>
					</div>
				</div>
			</ChatPanelLayout>
		);
	}

	if (interstitial !== null) {
		return (
			<ChatPanelLayout
				title={interstitial.title}
				isLoading={false}
				onBack={onBack}
				onClose={onClose}
				style={style}
			>
				<SuccessInterstitial
					title={interstitial.title}
					summary={interstitial.summary}
					secondaryAction={
						isStandalone ? undefined : { label: 'Close', onPress: onClose }
					}
					primaryAction={
						isStandalone
							? { label: 'Close', onPress: onClose }
							: { label: 'View campaign', onPress: handleViewCampaign }
					}
				/>
			</ChatPanelLayout>
		);
	}

	return (
		<ChatPanelLayout
			title={
				reviewMode === 'ownerOnly'
					? 'Review and start'
					: reviewMode === 'monitorOnly'
						? 'Start monitoring'
						: reviewMode === 'alwaysOnOnly'
							? 'Review and start'
							: 'Schedule'
			}
			actionRenderer={actionRenderer}
			isLoading={!snapshot}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{reviewMode === 'ownerOnly' ? (
				<OwnerOnlyReviewBody asks={ownerAsks} />
			) : reviewMode === 'monitorOnly' || reviewMode === 'alwaysOnOnly' ? (
				<NoDateReviewBody
					monitors={monitorChildren}
					alwaysOn={alwaysOnChildren}
					asks={ownerAsks}
				/>
			) : (
				<ScheduleBody
					postArtifacts={postArtifacts as InstagramPost[]}
					emailArtifacts={emailArtifacts}
					emailActionsByRef={emailActionsByRef}
					monitorActions={monitorActions}
					isStandalone={isStandalone}
					onPostModeChange={setIgToggleMode}
					onRowPendingChange={setRowPending}
				/>
			)}
		</ChatPanelLayout>
	);
};
